import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "../services/api";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaPlus, FaMinus, FaShieldAlt, FaTruck, FaExchangeAlt } from "react-icons/fa";
import AddToCartButton from "../components/AddToCartButton";
import ImageMagnifier from "../components/ImageMagnifier";
import ProductCard from "../components/ProductCard";
import ProductReviews from "../components/ProductReviews";
import ProductSchema from "../components/ProductSchema";
import WishlistHeart from "../components/WishlistHeart";
import { trackViewItem } from "../services/analytics";
import { Container, Row, Col } from "react-bootstrap";

const S = `
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@600;700&family=DM+Sans:wght@400;500;600&display=swap');

/* ─── Page wrapper ─── */
.pd-page {
  background: #f9f7f4;
  min-height: 100vh;
  font-family: 'DM Sans', sans-serif;
}

/* ─── Breadcrumb ─── */
.pd-breadcrumb {
  background: #fff;
  border-bottom: 1px solid #e8e4df;
  padding: 12px 0;
}
.pd-breadcrumb-inner {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  font-size: 0.82rem;
}
.pd-bc-link {
  color: #8a8a8a;
  text-decoration: none;
  transition: color 0.13s;
}
.pd-bc-link:hover { color: #1a5c38; }
.pd-bc-sep { color: #ccc; font-size: 0.75rem; }
.pd-bc-current { color: #1a1a1a; font-weight: 500; }

/* ─── Main container ─── */
.pd-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 36px 24px 60px;
}

/* ─── Product layout ─── */
.pd-layout {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 48px;
  align-items: start;
  background: #fff;
  border-radius: 20px;
  border: 1px solid #e8e4df;
  padding: 32px;
  margin-bottom: 36px;
}

/* ─── Images ─── */
.pd-images {}
.pd-main-img-wrap {
  position: relative;
  border-radius: 16px;
  overflow: hidden;
  background: #f9f7f4;
  border: 1px solid #e8e4df;
  margin-bottom: 12px;
}
.pd-sale-badge {
  position: absolute;
  top: 12px; left: 12px;
  background: #d94f3d;
  color: #fff;
  font-size: 0.75rem;
  font-weight: 700;
  padding: 4px 10px;
  border-radius: 8px;
  z-index: 10;
  letter-spacing: 0.04em;
}
.pd-thumbs {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
}
.pd-thumb {
  width: 72px; height: 72px;
  border-radius: 10px;
  border: 2px solid #e8e4df;
  object-fit: cover;
  cursor: pointer;
  transition: border-color 0.15s, transform 0.15s;
}
.pd-thumb:hover { border-color: #1a5c38; transform: scale(1.04); }
.pd-thumb.active { border-color: #1a5c38; }

/* ─── Product info ─── */
.pd-info {}
.pd-name {
  font-family: 'Playfair Display', serif;
  font-size: clamp(1.4rem, 2.5vw, 2rem);
  font-weight: 700;
  color: #1a1a1a;
  line-height: 1.2;
  margin-bottom: 6px;
}
.pd-name-kr {
  font-size: 0.9rem;
  color: #aaa;
  margin-bottom: 18px;
}

/* Sale badge inline */
.pd-sale-pill {
  display: inline-flex;
  align-items: center;
  background: #fef2f2;
  color: #d94f3d;
  font-size: 0.78rem;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 100px;
  margin-bottom: 14px;
  letter-spacing: 0.04em;
}

/* Price */
.pd-price-row {
  display: flex;
  align-items: baseline;
  gap: 12px;
  margin-bottom: 20px;
}
.pd-price {
  font-family: 'Playfair Display', serif;
  font-size: 1.9rem;
  font-weight: 700;
  color: #1a5c38;
}
.pd-price-old {
  font-size: 1rem;
  color: #bbb;
  text-decoration: line-through;
}

/* Meta info pills */
.pd-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 20px;
}
.pd-meta-pill {
  display: flex;
  align-items: center;
  gap: 6px;
  background: #f9f7f4;
  border: 1px solid #e8e4df;
  border-radius: 8px;
  padding: 6px 12px;
  font-size: 0.82rem;
  color: #4a4a4a;
}
.pd-meta-pill strong { color: #1a1a1a; }
.pd-stock-ok  { color: #1a5c38; font-weight: 600; }
.pd-stock-oos { color: #d94f3d; font-weight: 600; }

.pd-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 20px;
}
.pd-tag-badge {
  background: #eef6f0;
  color: #1a5c38;
  border-radius: 999px;
  padding: 4px 12px;
  font-size: 0.78rem;
  font-weight: 600;
}

/* Divider */
.pd-divider { height: 1px; background: #e8e4df; margin: 20px 0; }

/* Quantity */
.pd-qty-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
}
.pd-qty-label {
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #1a5c38;
  white-space: nowrap;
}
.pd-qty-controls {
  display: flex;
  align-items: center;
  gap: 0;
  border: 1.5px solid #e8e4df;
  border-radius: 11px;
  overflow: hidden;
}
.pd-qty-btn {
  width: 38px; height: 38px;
  display: flex; align-items: center; justify-content: center;
  background: #f9f7f4; border: none; cursor: pointer;
  font-size: 0.8rem; color: #4a4a4a;
  transition: background 0.13s, color 0.13s;
}
.pd-qty-btn:hover:not(:disabled) { background: #e8f3ed; color: #1a5c38; }
.pd-qty-btn:disabled { opacity: 0.4; cursor: not-allowed; }
.pd-qty-val {
  width: 48px; text-align: center;
  font-size: 0.95rem; font-weight: 600; color: #1a1a1a;
  border: none; outline: none; background: #fff;
  border-left: 1px solid #e8e4df;
  border-right: 1px solid #e8e4df;
  height: 38px; padding: 0;
  font-family: 'DM Sans', sans-serif;
}

/* Add to cart override */
.pd-info .cart-button {
  width: 100% !important;
  border-radius: 12px !important;
  height: 48px !important;
  min-height: 48px !important;
}
.pd-info .cart-button .add-to-cart {
  background: #1a5c38 !important;
  border-color: #1a5c38 !important;
  color: #fff !important;
  font-size: 0.95rem !important;
  font-weight: 600 !important;
  border-radius: 12px !important;
}
.pd-info .cart-button:hover .add-to-cart { background: #134429 !important; }
.pd-info .cart-button .added { background: #1a5c38 !important; border-radius: 12px !important; }
.pd-info .cart-button .btn-danger { border-radius: 12px !important; font-size: 0.9rem !important; }

/* Trust badges */
.pd-trust {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 20px;
}
.pd-trust-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.78rem;
  color: #6a6a6a;
}
.pd-trust-icon { color: #1a5c38; font-size: 0.85rem; }

/* ─── Tabs ─── */
.pd-tabs-wrap {
  background: #fff;
  border-radius: 20px;
  border: 1px solid #e8e4df;
  overflow: hidden;
  margin-bottom: 36px;
}
.pd-tabs-header {
  display: flex;
  border-bottom: 1px solid #e8e4df;
  background: #fafaf9;
}
.pd-tab-btn {
  background: none; border: none;
  font-size: 0.9rem; font-weight: 600;
  padding: 16px 24px; color: #8a8a8a;
  cursor: pointer; position: relative;
  transition: color 0.15s;
  font-family: 'DM Sans', sans-serif;
  white-space: nowrap;
}
.pd-tab-btn:hover { color: #1a1a1a; }
.pd-tab-btn.active { color: #1a5c38; }
.pd-tab-btn.active::after {
  content: '';
  position: absolute; left: 0; bottom: -1px;
  width: 100%; height: 2px;
  background: #1a5c38;
}
.pd-tab-content {
  padding: 28px 32px;
  font-size: 0.9rem;
  color: #4a4a4a;
  line-height: 1.7;
}
.pd-tab-content p, .pd-tab-content li { margin-bottom: 8px; }
.pd-tab-content h5 {
  font-family: 'Playfair Display', serif;
  font-size: 1.1rem; font-weight: 700;
  color: #1a1a1a; margin-bottom: 16px;
}
.pd-tab-content h6 {
  font-size: 0.88rem; font-weight: 700;
  color: #1a1a1a; margin-bottom: 8px; margin-top: 16px;
}
.pd-policy-section { margin-bottom: 20px; }
.pd-policy-section:last-child { margin-bottom: 0; }
.pd-policy-tag {
  display: inline-flex; align-items: center; gap: 6px;
  background: #e8f3ed; color: #1a5c38;
  font-size: 0.76rem; font-weight: 700; letter-spacing: 0.06em;
  text-transform: uppercase; padding: 4px 10px; border-radius: 6px;
  margin-bottom: 10px;
}

/* ─── Related products ─── */
.pd-related-wrap {
  margin-top: 8px;
}
.pd-related-title {
  font-family: 'Playfair Display', serif;
  font-size: 1.5rem; font-weight: 700;
  color: #1a1a1a; margin-bottom: 24px;
}

/* Loading / error */
.pd-loading {
  min-height: 60vh; display: flex;
  flex-direction: column; align-items: center; justify-content: center;
  gap: 14px; color: #8a8a8a;
  font-family: 'DM Sans', sans-serif;
}
.pd-spinner {
  width: 36px; height: 36px;
  border: 3px solid #e8e4df; border-top-color: #1a5c38;
  border-radius: 50%; animation: pdspin 0.8s linear infinite;
}
@keyframes pdspin { to { transform: rotate(360deg); } }

/* ─── Responsive ─── */
@media (max-width: 900px) {
  .pd-layout { grid-template-columns: 1fr; gap: 28px; padding: 22px; }
}
@media (max-width: 600px) {
  .pd-container { padding: 20px 14px 48px; }
  .pd-layout { padding: 16px; }
  .pd-tab-content { padding: 20px 16px; }
  .pd-tabs-header { overflow-x: auto; }
  .pd-tab-btn { padding: 14px 16px; font-size: 0.82rem; }
  .pd-thumb { width: 58px; height: 58px; }
  .pd-price { font-size: 1.6rem; }
}
`;

const ProductDetails = () => {
  const { product_id } = useParams();
  const navigate = useNavigate();

  const [product,         setProduct]         = useState(null);
  const [images,          setImages]          = useState([]);
  const [mainImage,       setMainImage]       = useState(null);
  const [quantity,        setQuantity]        = useState(1);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [productImages,   setProductImages]   = useState({});
  const [loading,         setLoading]         = useState(true);
  const [error,           setError]           = useState(null);
  const [subcategoryInfo, setSubcategoryInfo] = useState(null);
  const [activeTab,       setActiveTab]       = useState("description");
  const { t } = useTranslation();

  // ─── All original fetch logic preserved ───────────────────────────────────
  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true); setError(null);
        // Product + its images in parallel — both only need the id.
        const [res, imgRes] = await Promise.all([
          api.get(`/products/${product_id}`),
          api.get(`/product-images/${product_id}`).catch(() => ({ data: [] })),
        ]);
        const productData = res.data;
        setProduct(productData);
        trackViewItem(productData);
        const imgs = Array.isArray(imgRes.data) ? imgRes.data : [];
        setImages(imgs);
        setMainImage(imgs.find(i => i.is_main)?.url || imgs[0]?.url || "/images/default-product.jpg");

        // Show the product NOW — subcategory info and related products load
        // in the background instead of blocking the whole page.
        setLoading(false);

        const subCategoryId = getSubcategoryId(productData);
        if (subCategoryId) {
          api.get(`/subcategories/${subCategoryId}`)
            .then(r => setSubcategoryInfo(r.data))
            .catch(e => console.error("Subcategory fetch:", e));
        }
        fetchRelatedProducts(productData);
      } catch (e) {
        console.error("Error loading product:", e);
        setError(t("pd.loadError"));
        setLoading(false);
      }
    };
    if (product_id) fetchProduct();
  }, [product_id]); // eslint-disable-line react-hooks/exhaustive-deps

  // SEO title/meta description — same create-if-missing pattern as CMS.jsx,
  // with the same HTML-stripping trick for a fallback excerpt (description is
  // rich HTML now too).
  useEffect(() => {
    if (!product) return;
    const fallbackExcerpt = (product.description || "").replace(/<[^>]+>/g, "").slice(0, 160);
    document.title = product.seo_title || `${product.name} | FMART`;
    const desc = product.seo_meta_description || fallbackExcerpt;
    let metaDesc = document.querySelector("meta[name='description']");
    if (!metaDesc) {
      metaDesc = document.createElement("meta");
      metaDesc.name = "description";
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute("content", desc);
  }, [product]);

  const getSubcategoryId = (productData) => {
    const fields = ["sub_category_id","subcategory_id","subcategoryId","subCategory","subCategoryId"];
    for (const field of fields) {
      if (productData[field]) {
        if (typeof productData[field] === "string") return productData[field];
        if (typeof productData[field] === "object" && productData[field]._id) return productData[field]._id;
      }
    }
    return null;
  };

  const fetchRelatedProducts = async (productData) => {
    const subCategoryId = getSubcategoryId(productData);
    if (!subCategoryId) { setRelatedProducts([]); return; }
    try {
      let data = []; let ok = false;
      try { const r = await api.get(`/products/by-subcategories?ids=${subCategoryId}`); data = Array.isArray(r.data) ? r.data : []; ok = true; } catch {}
      if (!ok) try { const r = await api.get(`/subcategories/${subCategoryId}/products`); data = Array.isArray(r.data) ? r.data : []; ok = true; } catch {}
      if (!ok) try { const r = await api.get(`/categories/${subCategoryId}/products`);   data = Array.isArray(r.data) ? r.data : []; ok = true; } catch {}
      if (!ok) try { const r = await api.get(`/products?subcategory=${subCategoryId}`);  data = Array.isArray(r.data) ? r.data : []; } catch {}

      const related = data.filter(p => p._id !== product_id).slice(0, 8);
      setRelatedProducts(related);
      if (related.length > 0) {
        // The primary endpoint now ships imageUrl — zero extra requests.
        if (related.some(p => p.imageUrl !== undefined)) {
          setProductImages(related.reduce((acc, p) => ({ ...acc, [p._id]: p.imageUrl || "/images/default-product.jpg" }), {}));
        } else {
          await fetchRelatedProductImages(related); // legacy fallback endpoints
        }
      }
    } catch { setRelatedProducts([]); }
  };

  const fetchRelatedProductImages = async (products) => {
    const imagePromises = products.map(async product => {
      try {
        const res    = await api.get(`/product-images/${product._id}`);
        const imgs   = Array.isArray(res.data) ? res.data : [];
        const main   = imgs.find(i => i.is_main) || imgs[0];
        return { id: product._id, imageUrl: main?.url || "/images/default-product.jpg" };
      } catch {
        return { id: product._id, imageUrl: "/images/default-product.jpg" };
      }
    });
    const imgs     = await Promise.all(imagePromises);
    const imageMap = imgs.reduce((acc, { id, imageUrl }) => ({ ...acc, [id]: imageUrl }), {});
    setProductImages(imageMap);
  };

  const calculateSalePercentage = () => {
    if (!product?.is_on_sale) return 0;
    const orig = product.compare_price || product.sell_price;
    const sale = product.sale_price;
    if (orig && sale && orig > sale) return Math.round(((orig - sale) / orig) * 100);
    return 0;
  };

  const increaseQty = () => {
    const stockQty = product?.stock_quantity ?? 0;
    if (quantity < stockQty) setQuantity(prev => prev + 1);
  };
  const decreaseQty = () => setQuantity(prev => prev > 1 ? prev - 1 : 1);

  // ─── States ───────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <>
        <style>{S}</style>
        <div className="pd-loading">
          <div className="pd-spinner" />
          <span>{t("pd.loading")}</span>
        </div>
      </>
    );
  }
  if (error) {
    return (
      <>
        <style>{S}</style>
        <div className="pd-loading" style={{ color: "#d94f3d" }}>
          <span style={{ fontSize: "2rem" }}>⚠️</span>
          <span>{error}</span>
        </div>
      </>
    );
  }
  if (!product) {
    return (
      <>
        <style>{S}</style>
        <div className="pd-loading"><span>{t("pd.notFound")}</span></div>
      </>
    );
  }

  const price          = product.is_on_sale ? product.sale_price : product.sell_price;
  const oldPrice       = product.is_on_sale ? (product.compare_price || product.sell_price) : null;
  const stockQty       = product.stock_quantity ?? 0;
  const outOfStock     = stockQty === 0;
  const salePercentage = calculateSalePercentage();

  return (
    <>
      <style>{S}</style>
      <div className="pd-page">
        <ToastContainer position="bottom-right" autoClose={2000} />

        {/* ─── Breadcrumb ─── */}
        <div className="pd-breadcrumb">
          <div className="pd-breadcrumb-inner">
            <Link to="/" className="pd-bc-link">{t("nav.home")}</Link>
            {subcategoryInfo?.category_id && (
              <>
                <span className="pd-bc-sep">/</span>
                <Link to={`/categories/${subcategoryInfo.category_id._id}/products`} className="pd-bc-link">
                  {subcategoryInfo.category_id.name}
                </Link>
              </>
            )}
            {subcategoryInfo && (
              <>
                <span className="pd-bc-sep">/</span>
                <Link to={`/subcategory/${subcategoryInfo._id}`} className="pd-bc-link">
                  {subcategoryInfo.name}
                </Link>
              </>
            )}
            <span className="pd-bc-sep">/</span>
            <span className="pd-bc-current">{product.name}</span>
          </div>
        </div>

        <div className="pd-container">

          {/* ─── Product layout ─── */}
          <div className="pd-layout">

            {/* Images */}
            <div className="pd-images">
              <div className="pd-main-img-wrap">
                {salePercentage > 0 && (
                  <span className="pd-sale-badge">{t("pd.offBadge", { pct: salePercentage })}</span>
                )}
                <WishlistHeart productId={product._id} style={{ width: 42, height: 42, top: 12, right: 12, fontSize: 18 }} />
                <ImageMagnifier
                  src={mainImage || "/images/default-product.jpg"}
                  zoom={2}
                />
              </div>
              {images.length > 1 && (
                <div className="pd-thumbs">
                  {images.map((img, i) => (
                    <img
                      key={i}
                      src={img.url}
                      alt={`Thumbnail ${i + 1}`}
                      className={`pd-thumb ${mainImage === img.url ? "active" : ""}`}
                      onClick={() => setMainImage(img.url)}
                      onError={e => { e.target.src = "/images/default-product.jpg"; }}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Info */}
            <div className="pd-info">
              <h1 className="pd-name">{product.name}</h1>
              {product.name_kr && <p className="pd-name-kr">{product.name_kr}</p>}

              {salePercentage > 0 && (
                <span className="pd-sale-pill">🔥 {t("pd.offBadge", { pct: salePercentage })}</span>
              )}

              <div className="pd-price-row">
                <span className="pd-price">₩{price?.toLocaleString()}</span>
                {oldPrice && <span className="pd-price-old">₩{oldPrice.toLocaleString()}</span>}
              </div>

              {/* Meta pills */}
              <div className="pd-meta">
                {product.brand && (
                  <span className="pd-meta-pill"><strong>{t("pd.brand")}</strong> {product.brand}</span>
                )}
                {product.weight && (
                  <span className="pd-meta-pill"><strong>{t("pd.weight")}</strong> {product.weight}</span>
                )}
                <span className="pd-meta-pill">
                  <strong>{t("pd.stock")}</strong>
                  <span className={outOfStock ? "pd-stock-oos" : "pd-stock-ok"}>
                    {outOfStock ? t("product.outOfStock") : t("pd.available", { count: stockQty })}
                  </span>
                </span>
              </div>

              {product.tags?.length > 0 && (
                <div className="pd-tags">
                  {product.tags.map((tag) => <span key={tag} className="pd-tag-badge">{tag}</span>)}
                </div>
              )}

              <div className="pd-divider" />

              {/* Quantity */}
              {!outOfStock && (
                <div className="pd-qty-row">
                  <span className="pd-qty-label">{t("pd.qty")}</span>
                  <div className="pd-qty-controls">
                    <button className="pd-qty-btn" onClick={decreaseQty} disabled={quantity <= 1}>
                      <FaMinus />
                    </button>
                    <input
                      className="pd-qty-val"
                      type="number"
                      value={quantity}
                      min="1" max={stockQty}
                      onChange={e => {
                        const val = Number(e.target.value);
                        if (val >= 1 && val <= stockQty) setQuantity(val);
                      }}
                    />
                    <button className="pd-qty-btn" onClick={increaseQty} disabled={quantity >= stockQty}>
                      <FaPlus />
                    </button>
                  </div>
                </div>
              )}

              {/* Add to Cart */}
              <AddToCartButton
                product={product}
                quantity={quantity}
                customClass="atcb btn btn-warning fw-bold text-white"
              />

              {/* Trust badges */}
              <div className="pd-trust">
                <span className="pd-trust-item"><FaShieldAlt className="pd-trust-icon" /> {t("home.features.halal.title")}</span>
                <span className="pd-trust-item"><FaTruck className="pd-trust-icon" /> {t("pd.trustDelivery")}</span>
                <span className="pd-trust-item"><FaExchangeAlt className="pd-trust-icon" /> {t("pd.trustReturns")}</span>
              </div>
            </div>
          </div>

          {/* ─── Tabs ─── */}
          <div className="pd-tabs-wrap">
            <div className="pd-tabs-header">
              {["description", "additional", "refund"].map(tab => (
                <button
                  key={tab}
                  className={`pd-tab-btn ${activeTab === tab ? "active" : ""}`}
                  onClick={() => setActiveTab(tab)}
                >
                  {tab === "description" ? t("pd.tabDescription")
                   : tab === "additional" ? t("pd.tabAdditional")
                   : t("pd.tabRefund")}
                </button>
              ))}
            </div>

            <div className="pd-tab-content">
              {activeTab === "description" && (
                product.description
                  ? <div className="pd-description-html" dangerouslySetInnerHTML={{ __html: product.description }} />
                  : <p>{t("pd.noDescription")}</p>
              )}

              {activeTab === "additional" && (
                <div>
                  <p><strong>{t("pd.weight")}:</strong> {product.weight || t("pd.na")}</p>
                  <p><strong>{t("pd.brand")}:</strong> {product.brand || t("pd.na")}</p>
                </div>
              )}

              {activeTab === "refund" && (
                <div>
                  <h5>{t("pd.policy.title")}</h5>
                  <div className="pd-policy-section">
                    <div className="pd-policy-tag">📦 {t("pd.policy.shippingTag")}</div>
                    <p>{t("pd.policy.shippingText")}</p>
                  </div>
                  <div className="pd-policy-section">
                    <div className="pd-policy-tag">🔄 {t("pd.policy.exchangeTag")}</div>
                    <p>{t("pd.policy.exchangeText")}</p>
                  </div>
                  <div className="pd-policy-section">
                    <div className="pd-policy-tag">💰 {t("pd.policy.refundTag")}</div>
                    <h6>{t("pd.policy.conditions")}</h6>
                    <ul>
                      <li>{t("pd.policy.c1")}</li>
                      <li>{t("pd.policy.c2")}</li>
                      <li>{t("pd.policy.c3")}</li>
                      <li>{t("pd.policy.c4")}</li>
                    </ul>
                    <h6>{t("pd.policy.nonRefundable")}</h6>
                    <ul>
                      <li>{t("pd.policy.n1")}</li>
                      <li>{t("pd.policy.n2")}</li>
                      <li>{t("pd.policy.n3")}</li>
                    </ul>
                    <h6>{t("pd.policy.process")}</h6>
                    <ol>
                      <li>{t("pd.policy.p1")}</li>
                      <li>{t("pd.policy.p2")}</li>
                      <li>{t("pd.policy.p3")}</li>
                    </ol>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* ─── Customer reviews ─── */}
          {product?._id && <ProductSchema product={product} images={images} />}
          {product?._id && <ProductReviews productId={product._id} />}

          {/* ─── Related products ─── */}
          {relatedProducts.length > 0 && (
            <div className="pd-related-wrap">
              <h2 className="pd-related-title">{t("pd.related")}</h2>
              <Row className="g-3">
                {relatedProducts.map(prod => prod?._id ? (
                  <Col key={prod._id} xs={6} sm={6} md={4} lg={3} xl={2}
                    className="d-flex justify-content-center">
                    <ProductCard
                      product={prod}
                      productImageUrl={productImages[prod._id]}
                    />
                  </Col>
                ) : null)}
              </Row>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ProductDetails;